import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { makeRequest2 } from '../../../../../utils/makeRequest';
import { toastify } from '../../../../../utils/toast';
import { useFacilityStore } from '../../../../../app/z-store/store';
import { getGLAccountsURL } from '../../../../../utils/urls'; 

function ExpenseCategoryTab() {
    const selectedFacilityId = useFacilityStore((state) => state.facilityId);
    const [categoryTitle, setCategoryTitle] = useState('');
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingCategory, setEditingCategory] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    
    // New state for GL accounts
    const [glAccounts, setGlAccounts] = useState([]);
    const [loadingGLAccounts, setLoadingGLAccounts] = useState(false);
    const [glAccountsConfig, setGlAccountsConfig] = useState({
        invoice: {
            debit: '',
            credit: ''
        },
        payment: {
            debit: '',
            credit: ''
        }
    });

    // Fetch categories on component mount and when facility changes
    useEffect(() => {
        if (selectedFacilityId) {
            fetchCategories();
            fetchGLAccounts();
        }
    }, [selectedFacilityId]);

    // New function to fetch GL accounts
    const fetchGLAccounts = async () => {
        try {
            setLoadingGLAccounts(true);

            if (!selectedFacilityId) {
                console.error("No facility ID found");
                toastify("No facility selected. Please select a facility first.", "error");
                return;
            }

            console.log("Fetching GL accounts for facility:", selectedFacilityId);

            const response = await makeRequest2(`${getGLAccountsURL}/${selectedFacilityId}`, "GET");

            console.log("GL accounts API response:", response);

            if (response && response.success) {
                const accountsData = response.data?.data || [];
                setGlAccounts(accountsData);
                console.log("Fetched GL accounts successfully:", accountsData);
            } else {
                const errorMsg = response?.message || "Failed to fetch GL accounts.";
                console.error("GL accounts API error:", errorMsg);
                toastify(errorMsg, "error");
                setGlAccounts([]);
            }
        } catch (error) {
            console.error("Error in fetchGLAccounts:", error);
            toastify("Error fetching GL accounts. Please try again later.", "error");
            setGlAccounts([]);
        } finally {
            setLoadingGLAccounts(false);
        }
    };

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const response = await makeRequest2(
                `/api/app/expense_management/expense_category/get_all_expense_categories/${selectedFacilityId}`,
                'GET'
            );
            if (response.success) {
                setCategories(response.data?.data || []);
            } else {
                setCategories([]);
                toastify(response.error || 'Failed to fetch categories', 'error');
            }
        } catch (error) {
            setCategories([]);
            toastify('Error fetching categories', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleAddCategory = async () => {
        if (!categoryTitle.trim()) {
            toastify('Please enter a category title', 'error');
            return;
        }

        if (!selectedFacilityId) {
            toastify('Please select a facility', 'error');
            return;
        }

        // Validate GL accounts
        if (!glAccountsConfig.invoice.debit || !glAccountsConfig.invoice.credit || 
            !glAccountsConfig.payment.debit || !glAccountsConfig.payment.credit) {
            toastify('Please select all GL accounts for invoice and payment entries', 'error');
            return;
        }

        try {
            setLoading(true);
            const endpoint = isEditMode 
                ? `/api/app/expense_management/expense_category/update_expense_category/${selectedFacilityId}/${editingCategory._id}`
                : `/api/app/expense_management/expense_category/add_expense_category/${selectedFacilityId}`;
            
            const method = isEditMode ? 'PUT' : 'POST';
            
            const payload = {
                name: categoryTitle.trim(),
                glAccounts: glAccountsConfig
            };
            
            const response = await makeRequest2(
                endpoint,
                method,
                payload
            );

            if (response.success) {
                const message = isEditMode 
                    ? 'Category updated successfully' 
                    : 'Category added successfully';
                toastify(response.message || message, 'success');
                
                // Reset form
                resetForm();
                
                // Close modal
                const modalElement = document.getElementById('addExpenseCategoryModal');
                const modal = window.bootstrap?.Modal?.getInstance(modalElement);
                if (modal) {
                    modal.hide();
                }
                
                // Refresh categories
                fetchCategories();
            } else {
                toastify(response.error || 'Failed to save category', 'error');
            }
        } catch (error) {
            toastify('Error saving category', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleEditCategory = (category) => {
        setCategoryTitle(category.name);
        setEditingCategory(category);
        setIsEditMode(true);
        
        // Set GL accounts if they exist
        if (category.glAccounts) {
            setGlAccountsConfig({
                invoice: {
                    debit: category.glAccounts.invoice?.debit || '',
                    credit: category.glAccounts.invoice?.credit || ''
                },
                payment: {
                    debit: category.glAccounts.payment?.debit || '',
                    credit: category.glAccounts.payment?.credit || ''
                }
            });
        } else {
            // Reset GL accounts config if no existing data
            setGlAccountsConfig({
                invoice: { debit: '', credit: '' },
                payment: { debit: '', credit: '' }
            });
        }
    };

    const handleDeleteCategory = (categoryId) => {
        confirmDialog({
            message: 'Are you sure you want to delete this category? This action cannot be undone.',
            header: 'Delete Confirmation',
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                try {
                    setLoading(true);
                    const response = await makeRequest2(
                        `/api/app/expense_management/expense_category/delete_expense_category/${selectedFacilityId}/${categoryId}`,
                        'DELETE'
                    );
                    
                    if (response.success) {
                        toastify(response.message || 'Category deleted successfully', 'success');
                        fetchCategories();
                    } else {
                        toastify(response.error || 'Failed to delete category', 'error');
                    }
                } catch (error) {
                    toastify('Error deleting category', 'error');
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const resetForm = () => {
        setCategoryTitle('');
        setIsEditMode(false);
        setEditingCategory(null);
        setGlAccountsConfig({
            invoice: { debit: '', credit: '' },
            payment: { debit: '', credit: '' }
        });
    };

    const handleGLAccountChange = (section, type, value) => {
        setGlAccountsConfig(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [type]: value
            }
        }));
    };

    // Filter categories based on search term
    const filteredCategories = Array.isArray(categories)
        ? categories.filter((category) => {
            const title = (category.name || '').toLowerCase();
            return title.includes(searchTerm.toLowerCase());
        })
        : [];

    // Format date for display
    const formatDate = (dateString) => {
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return "Invalid Date";
            
            const day = date.getDate().toString().padStart(2, '0');
            const month = date.toLocaleString('en-US', { month: 'long' }).toUpperCase();
            const year = date.getFullYear();
            
            return `${day}-${month}-${year}`;
        } catch (error) {
            return "Invalid Date";
        }
    };

    // Action template for table
    const actionTemplate = (rowData) => {
        return (
            <div className="d-flex gap-2">
                <Button
                    icon="ti ti-edit"
                    className="p-button-rounded p-button-info"
                    onClick={() => {
                        handleEditCategory(rowData);
                        // Trigger modal open
                        const modalElement = document.getElementById('addExpenseCategoryModal');
                        const modal = new window.bootstrap.Modal(modalElement);
                        modal.show();
                    }}
                    tooltip="Edit Category"
                    size="small"
                />
                {/* <Button
                    icon="ti ti-trash"
                    className="p-button-rounded p-button-danger"
                    onClick={() => handleDeleteCategory(rowData._id)}
                    tooltip="Delete Category"
                    size="small"
                /> */}
            </div>
        );
    };

    if (!selectedFacilityId) {
        return (
            <div className="row">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-body text-center">
                            Please select a facility to manage expense categories.
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="row">
            <div className="col-lg-12 col-xxl-12">
                <div className="card">
                    <ConfirmDialog />
                    <div className="card-body">
                        {/* Search and Add Button */}
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <input
                                    className="form-control"
                                    placeholder="Search categories..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="col-md-6 text-end">
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    data-bs-toggle="modal"
                                    data-bs-target="#addExpenseCategoryModal"
                                    onClick={resetForm}
                                >
                                    Add Expense Category
                                </button>
                            </div>
                        </div>

                        {/* Categories Table */}
                        <div className="table-responsive">
                            <DataTable
                                value={filteredCategories}
                                emptyMessage="No expense categories found."
                                sortMode="multiple"
                                paginator
                                rows={10}
                                stripedRows
                                loading={loading}
                                tableStyle={{ minWidth: '30rem' }}
                                stateStorage="session"
                                stateKey="expense-categories-state"
                            >
                                <Column 
                                    field="name" 
                                    header="CATEGORY NAME" 
                                    sortable 
                                />
                                <Column 
                                    field="createdAt" 
                                    header="DATE CREATED" 
                                    sortable 
                                    body={(rowData) => formatDate(rowData.createdAt)}
                                />
                                <Column 
                                    header="ACTIONS" 
                                    body={actionTemplate} 
                                    style={{ width: '10rem' }} 
                                />
                            </DataTable>
                        </div>

                        {/* Add/Edit Category Modal */}
                        <div
                            className="modal fade"
                            id="addExpenseCategoryModal"
                            tabIndex="-1"
                            aria-labelledby="addExpenseCategoryModalLabel"
                            aria-hidden="true"
                        >
                            <div className="modal-dialog modal-lg">
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h5
                                            className="modal-title"
                                            id="addExpenseCategoryModalLabel"
                                        >
                                            {isEditMode ? 'Edit Category' : 'New Category'}
                                        </h5>
                                        <button
                                            type="button"
                                            className="btn-close"
                                            data-bs-dismiss="modal"
                                            aria-label="Close"
                                            onClick={resetForm}
                                        ></button>
                                    </div>
                                    <div className="modal-body">
                                        <form onSubmit={(e) => {e.preventDefault(); handleAddCategory();}}>
                                            <div className="row">
                                                {/* Category Name */}
                                                <div className="col-12 mb-3">
                                                    <label
                                                        htmlFor="category-title"
                                                        className="col-form-label pt-0"
                                                    >
                                                        Category Title: <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        id="category-title"
                                                        value={categoryTitle}
                                                        onChange={(e) => setCategoryTitle(e.target.value)}
                                                        placeholder="Enter category name"
                                                        required
                                                    />
                                                </div>

                                                {/* GL Accounts Section */}
                                                <div className="col-12">
                                                    <hr className="my-3" />
                                                    <h6 className="mb-3 text-primary">GL Account Configuration</h6>
                                                    {loadingGLAccounts && (
                                                        <div className="alert alert-info">
                                                            Loading GL accounts...
                                                        </div>
                                                    )}
                                                    {!loadingGLAccounts && glAccounts.length === 0 && (
                                                        <div className="alert alert-warning">
                                                            No GL accounts found. Please create GL accounts first.
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Invoice GL Accounts */}
                                                {glAccounts.length > 0 && (
                                                    <>
                                                        <div className="col-12 mb-2">
                                                            <h6>Expense Invoice GL Entries</h6>
                                                        </div>

                                                        <div className="col-md-6 mb-3">
                                                            <label className="form-label">Invoice Debit Account<span className="text-danger">*</span></label>
                                                            <select
                                                                className="form-control"
                                                                value={glAccountsConfig.invoice.debit}
                                                                onChange={(e) => handleGLAccountChange('invoice', 'debit', e.target.value)}
                                                                required
                                                            >
                                                                <option value="">-- Select Debit Account --</option>
                                                                {glAccounts.map((account) => (
                                                                    <option key={account._id} value={account._id}>
                                                                        {account.accountCode} - {account.accountName} ({account.accountType})
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            <small className="text-muted">Account to be debited when expenses are recorded</small>
                                                        </div>

                                                        <div className="col-md-6 mb-3">
                                                            <label className="form-label">Invoice Credit Account<span className="text-danger">*</span></label>
                                                            <select
                                                                className="form-control"
                                                                value={glAccountsConfig.invoice.credit}
                                                                onChange={(e) => handleGLAccountChange('invoice', 'credit', e.target.value)}
                                                                required
                                                            >
                                                                <option value="">-- Select Credit Account --</option>
                                                                {glAccounts.map((account) => (
                                                                    <option key={account._id} value={account._id}>
                                                                        {account.accountCode} - {account.accountName} ({account.accountType})
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            <small className="text-muted">Account to be credited when expenses are recorded</small>
                                                        </div>

                                                        {/* Payment GL Accounts */}
                                                        <div className="col-12 mb-2">
                                                            <h6>Expense Payment GL Entries</h6>
                                                        </div>

                                                        <div className="col-md-6 mb-3">
                                                            <label className="form-label">Payment Debit Account<span className="text-danger">*</span></label>
                                                            <select
                                                                className="form-control"
                                                                value={glAccountsConfig.payment.debit}
                                                                onChange={(e) => handleGLAccountChange('payment', 'debit', e.target.value)}
                                                                required
                                                            >
                                                                <option value="">-- Select Debit Account --</option>
                                                                {glAccounts.map((account) => (
                                                                    <option key={account._id} value={account._id}>
                                                                        {account.accountCode} - {account.accountName} ({account.accountType})
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            <small className="text-muted">Account to be debited when payments are made</small>
                                                        </div>

                                                        <div className="col-md-6 mb-3">
                                                            <label className="form-label">Payment Credit Account<span className="text-danger">*</span></label>
                                                            <select
                                                                className="form-control"
                                                                value={glAccountsConfig.payment.credit}
                                                                onChange={(e) => handleGLAccountChange('payment', 'credit', e.target.value)}
                                                                required
                                                            >
                                                                <option value="">-- Select Credit Account --</option>
                                                                {glAccounts.map((account) => (
                                                                    <option key={account._id} value={account._id}>
                                                                        {account.accountCode} - {account.accountName} ({account.accountType})
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            <small className="text-muted">Account to be credited when payments are made</small>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </form>
                                    </div>
                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            data-bs-dismiss="modal"
                                            onClick={resetForm}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={handleAddCategory}
                                            disabled={loading || loadingGLAccounts || glAccounts.length === 0}
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    {isEditMode ? 'Updating...' : 'Adding...'}
                                                </>
                                            ) : (
                                                isEditMode ? 'Update Category' : 'Add Category'
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ExpenseCategoryTab;